export interface LyricLine {
  time: number;     // seconds
  text: string;
}

// Engine proxy — Discord Activity iframes can only fetch URL-mapped origins,
// so we route LRCLIB through our own /api/lyrics endpoint.
const LYRICS_URL = '/api/lyrics';
const LRC_LINE_RE = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\]/g;

/**
 * Parse LRC-format text into time-ordered lyric lines.
 * Supports multi-tag lines (the same text appearing at multiple
 * timestamps) and silently skips metadata lines like [ti:...] or
 * lines without any timestamp tag.
 */
export function parseLrc(lrc: string): LyricLine[] {
  if (!lrc) return [];
  const out: LyricLine[] = [];
  for (const raw of lrc.split('\n')) {
    const matches = [...raw.matchAll(LRC_LINE_RE)];
    if (matches.length === 0) continue;
    // Text is whatever follows the last timestamp tag
    const lastMatch = matches[matches.length - 1];
    const textStart = (lastMatch.index ?? 0) + lastMatch[0].length;
    const text = raw.slice(textStart).trim();
    if (!text) continue;
    for (const m of matches) {
      const min = parseInt(m[1], 10);
      const sec = parseInt(m[2], 10);
      const frac = m[3] ? parseInt(m[3].padEnd(3, '0').slice(0, 3), 10) / 1000 : 0;
      out.push({ time: min * 60 + sec + frac, text });
    }
  }
  out.sort((a, b) => a.time - b.time);
  return out;
}

/**
 * Fetch synced lyrics from LRCLIB. Returns parsed lines on success,
 * null on 404, instrumental track, no synced lyrics, or network
 * error. Caller falls through to the visualizer on null.
 */
export interface LyricsResult {
  lines: LyricLine[];
  songDurationSeconds: number;  // 0 if unknown
}

export async function fetchLyrics(artist: string, track: string, fullTitle?: string): Promise<LyricsResult | null> {
  const params = new URLSearchParams({ artist, track });
  if (fullTitle) params.set('full_title', fullTitle);
  const url = `${LYRICS_URL}?${params.toString()}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const body = await r.json();
    if (body.instrumental) return null;
    const sync = body.syncedLyrics;
    if (!sync || typeof sync !== 'string') return null;
    const parsed = parseLrc(sync);
    if (parsed.length === 0) return null;
    return { lines: parsed, songDurationSeconds: Number(body.duration) || 0 };
  } catch {
    return null;
  }
}
